import React, {useState, useMemo, useEffect} from 'react'
import cx from 'classnames'

/* Components */
import AdultProductImagePlaceholder from '../../views/product/adult/image/AdultProductImagePlaceholder'
import HbButton from '../HbButton/HbButton'
import HbImageView from '../HbImage/view/HbImageView'
import Rating from '../Rating/Rating'
import ComparedProductsPlaceholder from './ComparedProductsPlaceholder'
import leoProductEvents from '../../events/leo/productEvents';

// Actions
import {actionCreators as productActionCreators} from '../../redux/actions/productActions'

// Constants
import {DAVINCI_PAGE_TYPES, DAVINCI_EVENT_NAMES} from '../../modules/analytics/davinci/davinciConstants'
import {
  ADULT_PRODUCT_IMAGE_PLACEHOLDER_SIZES,
  ADULT_PRODUCT_IMAGE_PLACEHOLDER_VIEW_TYPES,
} from '../../views/product/adult/image/AdultProductImagePlaceholderConstants'

// Styles
import s from './ComparedProducts.scss'


const MAX_VARIANT_CLASSIFICATION_VIEW_COUNT = 7
const MIN_PRODUCT_COUNT_TO_RENDER = 3

const formatPrice = (price) => {
  if (isNaN(price)) {
    return null
  }

  const formatter = new Intl.NumberFormat('tr-TR', {
    minimumFractionDigits: 2,
  })

  return `${formatter.format(price)} TL`
}

const ComparedProducts = ({
  brand,
  categoryId,
  className,
  dispatch,
  isBot,
  pageType,
  placeholderClassName,
  products,
  showLastUpdatedDate,
  title,
  sku,
  searchTerm
}) => {
  const [showMore, setShowMore] = useState(false)
  const [showAddToCartButtonIndex, setShowAddToCartButtonIndex] = useState(false)
  const [isAddToCartPendingId, setIsAddToCartPendingId] = useState(null)

  const classifications = useMemo(() => {
    const variants = ['Fiyat', 'Değerlendirmeler', 'Satıcı']

    products?.forEach((product) => {
      product?.classifications?.forEach((classification) => {
        if (!variants.includes(classification?.name)) {
          variants.push(classification?.name)
        }
      })
    })

    return variants
  }, [products])

  const showMoreButtonVisible = classifications.length > MAX_VARIANT_CLASSIFICATION_VIEW_COUNT

  let lastUpdatedDate = null
  if (showLastUpdatedDate) {
    lastUpdatedDate = new Date().toLocaleDateString('tr-TR', {year: 'numeric', month: 'long'})
  }

  useEffect(() => {
    if (!products?.length) {
      dispatch(
        productActionCreators.getComparedProducts.trigger({
          brand,
          searchTerm,
          categoryId,
          sku,
          pageType
        }),
      )
    }
  }, [])

  useEffect(() => {
    if (products?.length) {
      handleProductViewEvent()
    }
  }, [products])

  const addToCartClickEvent = (product,index) => {
    let data = {
      canonical_url: window?.utagData ? window.utagData.canonical_url : '',
      location: 'FeaturedSnippet',
      merchant: product?.listing?.MerchantName || '',
      page_type: pageType,
      page_value: pageType === DAVINCI_PAGE_TYPES.PRODUCT_DETAIL_PAGE ? sku : '',
      products: [{
        id: product?.productId || '',
        listingId: product?.listing?.ListingId || '',
        merchantId: product?.listing?.MerchantId,
        name: product?.name,
        position: index + 1,
        price: product?.listing?.FinalPrice?.toFixed(2),
        quantity: 1,
        sku: product?.sku || '',
      }],
      review_rate: product?.customerReviewRating || ''
    };

    window?.hepsiBus?.track(DAVINCI_EVENT_NAMES.ADD_TO_CART_CLICK, data, window.hepsiBus.parsers.getGlobals());
    leoProductEvents.sendAddToCartClickEvent({
      productId:  product?.productId || '',
      position: index + 1,
      sku: product?.sku || '',
      location: 'featured_snipped',
    })

  }

  const handleProductViewEvent = () => {
    let data = {
      page_type: pageType,
      location: 'FeaturedSnippet',
      page_value: pageType === DAVINCI_PAGE_TYPES.PRODUCT_DETAIL_PAGE ? sku : '',
      products: products?.map((item,index) => ({
        canonical_url: window?.utagData ? window.utagData.canonical_url : '',
        listing_id: item?.listing?.ListingId || '',
        product_id: item?.productId || '',
        price: item?.listing?.FinalPrice?.toFixed(2),
        sku: item?.sku || '',
        merchant: item?.listing?.MerchantName || '',
        merchantId: item?.listing?.MerchantId,
        review_rate: item?.customerReviewRating || '',
        position: index + 1,
      })),
    };

    window?.hepsiBus?.track(DAVINCI_EVENT_NAMES.PRODUCT_CAROUSEL_VIEW, data);

  }

  const handleAddToCart = (product,index) => {

    if (window?.Checkout?.AddToCart) {
      setIsAddToCartPendingId(product?.productId)

      const addToCardData = {
        listingId: product?.listing?.ListingId || '',
        sku: product?.sku || '',
        name: product?.name || '',
        quantity: 1,
      }

      const addToCartPageType = pageType === DAVINCI_PAGE_TYPES.PRODUCT_DETAIL_PAGE ? 'SfProductDetail' : 'SfProductList'

      window?.Checkout?.AddToCart(addToCardData, addToCartPageType, {
        callback: () => {
          setIsAddToCartPendingId(null)
        },
        errorHandler: () => {
          setIsAddToCartPendingId(null)
          console.error('Checkout Voltran Add To Cart Error')
        },
      })

      addToCartClickEvent(product,index)

    }
  }

  const handleProductClick = (product,index) => {
    let data = {
      canonical_url: window?.utagData ? window.utagData.canonical_url : '',
      location: 'FeaturedSnippet',
      merchant: product?.listing?.MerchantName || '',
      merchantId: product?.listing?.MerchantId,
      page_type: pageType,
      page_value: pageType === DAVINCI_PAGE_TYPES.PRODUCT_DETAIL_PAGE ? sku : '',
      products: [{
        id: product?.productId || '',
        listingId: product?.listing?.ListingId || '',
        mainProduct: false,
        merchantId: product?.listing?.MerchantId,
        name: product?.name,
        position: index + 1,
        price: product?.listing?.FinalPrice?.toFixed(2),
        quantity: 1,
        sku: product?.sku || '',
      }],
      customerReviewCount: product?.customerReviewCount || '',
      review_rate: product?.customerReviewRating || ''
    };

    window?.hepsiBus?.track(DAVINCI_EVENT_NAMES.PRODUCT_CAROUSEL_CLICK, data);

  }

  if (products.length < MIN_PRODUCT_COUNT_TO_RENDER) {
    return <ComparedProductsPlaceholder className={placeholderClassName} />
  }

  return (
    <section className={cx(s.container, className)}>
      <div className={s.head}>
        <h2 className={s.title} data-test-id='fs-title'>{title}</h2>
        {showLastUpdatedDate && <em className={s.text} data-test-id='fs-subtitle'>Son güncelleme: {lastUpdatedDate}</em>}
      </div>

      <div className={cx(s.compareTableWrapper, {[s.minimizeView]: !isBot && !showMore && showMoreButtonVisible})}>
        <table className={s.compareTable} data-test-id='fs-table'>
          <thead>
            <tr>
              <th></th>
              {products?.map((product, index) => (
                <th
                  onMouseEnter={() => setShowAddToCartButtonIndex(index)}
                  onMouseLeave={() => setShowAddToCartButtonIndex(null)}
                  key={product?.productId}>
                  <div className={s.productCard}>
                    <a
                      href={product?.url}
                      onClick={() => handleProductClick(product,index)}
                      data-test-id='fs-product-card'>
                      <div className={s.image}>
                        {product?.isAdultProduct ? (
                          <AdultProductImagePlaceholder
                            className={s.adult}
                            viewType={ADULT_PRODUCT_IMAGE_PLACEHOLDER_VIEW_TYPES.COLORED}
                            size={ADULT_PRODUCT_IMAGE_PLACEHOLDER_SIZES.MEDIUM}/>
                        ) : (
                          <HbImageView
                            shouldLoadEagerly={isBot}
                            image={{
                              src: product?.media?.LinkFormat,
                              alt: product?.name,
                              size: '148-148',
                            }}/>
                        )}
                      </div>
                      <span
                        className={s.name}
                        data-test-id={index === 0 ? product?.name : null}>
                        {product?.name}
                      </span>
                    </a>
                    {showAddToCartButtonIndex === index && (
                      <HbButton
                        text='Sepete ekle'
                        dataTestId='fs-add-to-cart-button'
                        onClick={() => handleAddToCart(product,index)}
                        isDisabled={product?.productId === isAddToCartPendingId}
                        className={s.addToCartButton}/>
                    )}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {classifications?.map((classificationName, index) => (
              <tr key={index} data-test-id='fs-classification-row'>
                <td>
                  <em className={s.variant}>{classificationName}</em>
                </td>
                {products?.map((product) => {
                  const classification = product?.classifications?.find(
                    (_variant) => _variant?.name === classificationName,
                  )

                  if (classificationName === 'Fiyat') {
                    return <td>{formatPrice(product?.listing?.FinalPrice)}</td>
                  }

                  if (classificationName === 'Değerlendirmeler') {
                    if (product?.customerReviewRating === 0) {
                      return <td>-</td>
                    } else {
                      return (
                        <td>
                          <div className={s.review}>
                            <span className={s.score}>{product?.customerReviewRating?.toFixed(1)?.replace('.', ',')}</span>
                            <Rating rate={product?.customerReviewRating} />
                          </div>
                        </td>
                      )
                    }
                  }

                  if (classificationName === 'Satıcı') {
                    return <td key={product?.productId}>{product?.listing?.MerchantName}</td>
                  }

                  return <td key={product?.productId}>{classification?.displayValue || '-'}</td>
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {!isBot && !showMore && showMoreButtonVisible && (
        <div className={s.footer}>
          <HbButton
            className={s.showMoreFeatures}
            dataTestId='fs-show-more-button'
            text='Daha fazla özellik göster'
            hasIcon
            onClick={() => setShowMore(true)}
            contentClassName={s.showMoreFeaturesIcon}/>
        </div>
      )}
    </section>
  )
}

export default ComparedProducts
